import { useEffect, useRef, useState } from 'react';
import { addSeconds } from 'date-fns';
import Cookies from 'js-cookie';

import { getSeconds, parseTimeForTimer } from 'core/utils/functions';
import { TimerValueReturned } from 'core/types';
import { removeStorageRetryAfter, setStorageRetryAfter } from '../../utils';

interface UseDateTimerProps {
  initialTime?: number;
  timerName: string;
  separator?: string;
  step?: number;
  isUseUnmount?: boolean;
}

type ShowTimer = (data?: { seconds?: number; isClear?: boolean }) => void;
type HideTimer = (data?: { seconds?: number; isClear?: boolean }) => void;

export interface UseTimerResponse {
  timerValue: TimerValueReturned;
  isShowTimer: boolean;
  showTimer: ShowTimer;
  hideTimer: HideTimer;
}

type UseTimer = (props: UseDateTimerProps) => UseTimerResponse;

const defaultTimerValues = {
  secondString: '00',
  hoursString: '00',
  secondsNumber: 0,
  minutesString: '00',
  fullString: '00:00:00',
  isMounted: false,
};

export const useTimer: UseTimer = (props) => {
  const { timerName, initialTime = 0, step = 1000, isUseUnmount = true, separator } = props;

  const [timerValue, setTimerValue] = useState<TimerValueReturned>(defaultTimerValues);
  const [isShowTimer, setIsShowTimer] = useState<boolean>(true);

  const dateRef = useRef<Date>(new Date());

  const updateSeconds = () => getSeconds(initialTime, timerName, dateRef.current);

  const updateDateRef = () => {
    dateRef.current = new Date();
  };

  const updateTimer = () => {
    const seconds = updateSeconds();
    updateDateRef();

    setStorageRetryAfter(seconds.toString());

    if (seconds < 0) {
      setIsShowTimer(false);
      setTimerValue(parseTimeForTimer(0));
      removeStorageRetryAfter();
    } else setTimerValue(parseTimeForTimer(seconds));
  };

  useEffect(() => {
    updateTimer();

    return () => {
      isUseUnmount && Cookies.remove(timerName);
    };
  }, []);

  useEffect(() => {
    if (!isShowTimer) return;
    updateDateRef();

    const intervalId = setInterval(updateTimer, step);

    return () => {
      clearInterval(intervalId);
    };
  }, [isShowTimer]);

  const toggleTimer = (isClear = true, seconds = 0, isShow: boolean) => {
    isClear && Cookies.remove(timerName);
    updateDateRef();
    seconds && Cookies.set(timerName, addSeconds(dateRef.current, seconds));
    setTimerValue(isShow ? parseTimeForTimer(updateSeconds(), separator) : defaultTimerValues);
    setIsShowTimer(isShow);
  };

  const showTimer: ShowTimer = ({ isClear, seconds } = {}) => toggleTimer(isClear, seconds, true);

  const hideTimer: HideTimer = ({ isClear, seconds } = {}) => toggleTimer(isClear, seconds, false);

  return { timerValue, isShowTimer, showTimer, hideTimer };
};
